<script setup>
import { ref, computed } from 'vue';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  message: {
    type: String,
    default: '',
  },
});

const dialogRef = ref(null);
const selectedAction = ref('');

const actions = [
  {
    id: 'correct',
    icon: 'i-lucide-spell-check-2',
    title: 'Corrigir texto',
    description:
      'Corrige ortografia, gramática e pontuação sem mudar a intenção da mensagem.',
  },
  {
    id: 'improve',
    icon: 'i-lucide-wand-sparkles',
    title: 'Melhorar mensagem',
    description:
      'Deixa a resposta mais clara, profissional e natural para o atendimento.',
  },
  {
    id: 'objection',
    icon: 'i-lucide-messages-square',
    title: 'Ajudar com objeção',
    description:
      'Ajuda a construir uma resposta comercial para dúvidas e objeções do lead.',
  },
];

const selectedActionData = computed(() =>
  actions.find(action => action.id === selectedAction.value)
);

const hasMessage = computed(() => Boolean(props.message?.trim()));

const selectAction = actionId => {
  selectedAction.value = actionId;
};

const reset = () => {
  selectedAction.value = '';
};

const open = () => {
  reset();
  dialogRef.value?.open();
};

const close = () => {
  reset();
  dialogRef.value?.close();
};

defineExpose({
  open,
  close,
});
</script>

<template>
  <Dialog
    ref="dialogRef"
    title="Assistente Comercial"
    description="Escolha como você quer melhorar o atendimento."
    width="lg"
    position="center"
    :show-confirm-button="false"
    :show-cancel-button="false"
    @close="reset"
  >
    <div class="mayara-assistant">
      <div class="mayara-assistant-intro">
        <div class="mayara-assistant-brand">
          <Icon icon="i-lucide-sparkles" class="size-5" />
        </div>

        <div>
          <p class="mayara-assistant-intro-title">
            Sua resposta continua sob seu controle
          </p>
          <p class="mayara-assistant-intro-text">
            O assistente vai sugerir melhorias. Você poderá revisar e editar
            antes de enviar ao paciente.
          </p>
        </div>
      </div>

      <div class="mayara-actions">
        <button
          v-for="action in actions"
          :key="action.id"
          type="button"
          class="mayara-action"
          :class="{ 'mayara-action--selected': selectedAction === action.id }"
          @click="selectAction(action.id)"
        >
          <div class="mayara-action-icon">
            <Icon :icon="action.icon" class="size-5" />
          </div>

          <div class="mayara-action-content">
            <p class="mayara-action-title">
              {{ action.title }}
            </p>
            <p class="mayara-action-description">
              {{ action.description }}
            </p>
          </div>

          <Icon
            icon="i-lucide-chevron-right"
            class="mayara-action-chevron size-4"
          />
        </button>
      </div>

      <div
        v-if="selectedActionData"
        class="mayara-selection"
      >
        <div class="mayara-selection-header">
          <Icon
            :icon="selectedActionData.icon"
            class="size-4"
          />
          <span>{{ selectedActionData.title }}</span>
        </div>

        <p v-if="hasMessage" class="mayara-selection-text">
          A mensagem digitada será usada como base. A integração com o
          assistente será ativada na próxima etapa.
        </p>

        <p v-else class="mayara-selection-text">
          Digite uma mensagem no campo de resposta para usar esta ação.
        </p>
      </div>

      <div class="mayara-assistant-footer">
        <NextButton
          label="Fechar"
          color="slate"
          variant="faded"
          sm
          @click="close"
        />

        <NextButton
          label="Em breve"
          icon="i-lucide-sparkles"
          color="blue"
          variant="solid"
          sm
          disabled
        />
      </div>
    </div>
  </Dialog>
</template>

<style scoped>
.mayara-assistant {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.mayara-assistant-intro {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.875rem;
  border: 1px solid rgb(var(--iris-6));
  border-radius: 0.875rem;
  background: rgb(var(--iris-3));
}

.mayara-assistant-brand {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 0.75rem;
  background: rgb(var(--iris-9));
  color: white;
}

.mayara-assistant-intro-title {
  margin: 0;
  color: rgb(var(--slate-12));
  font-size: 0.875rem;
  font-weight: 600;
}

.mayara-assistant-intro-text {
  margin: 0.25rem 0 0;
  color: rgb(var(--slate-11));
  font-size: 0.75rem;
  line-height: 1.125rem;
}

.mayara-actions {
  display: grid;
  gap: 0.625rem;
}

.mayara-action {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem;
  border: 1px solid rgb(var(--slate-5));
  border-radius: 0.875rem;
  background: rgb(var(--slate-1));
  color: rgb(var(--slate-12));
  text-align: left;
  cursor: pointer;
  transition:
    border-color 120ms ease,
    background 120ms ease,
    transform 120ms ease;
}

.mayara-action:hover {
  border-color: rgb(var(--iris-7));
  background: rgb(var(--iris-2));
  transform: translateY(-1px);
}

.mayara-action--selected {
  border-color: rgb(var(--iris-8));
  background: rgb(var(--iris-3));
}

.mayara-action-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.75rem;
  background: rgb(var(--iris-4));
  color: rgb(var(--iris-11));
}

.mayara-action-content {
  flex: 1;
  min-width: 0;
}

.mayara-action-title {
  margin: 0;
  color: rgb(var(--slate-12));
  font-size: 0.875rem;
  font-weight: 600;
}

.mayara-action-description {
  margin: 0.1875rem 0 0;
  color: rgb(var(--slate-10));
  font-size: 0.75rem;
  line-height: 1.125rem;
}

.mayara-action-chevron {
  flex: 0 0 auto;
  color: rgb(var(--slate-9));
}

.mayara-selection {
  padding: 0.875rem;
  border-radius: 0.875rem;
  background: rgb(var(--slate-3));
}

.mayara-selection-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: rgb(var(--slate-12));
  font-size: 0.8125rem;
  font-weight: 600;
}

.mayara-selection-text {
  margin: 0.375rem 0 0;
  color: rgb(var(--slate-10));
  font-size: 0.75rem;
  line-height: 1.125rem;
}

.mayara-assistant-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding-top: 0.25rem;
}
</style>
